import posthog, { type CaptureResult } from 'posthog-js';

import { getCookieConsent } from './cookieConsent';

const posthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY?.trim();

/**
 * Drop query strings and fragments from every URL-like property before it
 * leaves the browser. Pages such as /verify?email=... and Stripe's
 * ?session_id=... would otherwise put personal data into PostHog.
 */
function stripUrl(value: unknown): unknown {
    if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return value;
    try {
        const url = new URL(value);
        return `${url.origin}${url.pathname}`;
    } catch {
        return value.split(/[?#]/)[0];
    }
}

function stripUrlProps(props: Record<string, unknown> | undefined): void {
    if (!props) return;
    for (const key of Object.keys(props)) {
        if (/(url|referrer)$/i.test(key)) props[key] = stripUrl(props[key]);
    }
}

export function sanitizeEvent(event: CaptureResult | null): CaptureResult | null {
    if (!event) return event;
    stripUrlProps(event.properties);
    stripUrlProps(event.$set as Record<string, unknown> | undefined);
    stripUrlProps(event.$set_once as Record<string, unknown> | undefined);
    return event;
}

/** Bring PostHog in line with the shared banner choice (cookie_consent_v1 on .kontomatch.com). */
export function applyStoredConsent(): void {
    const consent = getCookieConsent();
    if (!consent) return;
    const status = posthog.get_explicit_consent_status();
    if (consent.analytics && status !== 'granted') {
        posthog.opt_in_capturing({ captureEventName: false });
    } else if (!consent.analytics && status !== 'denied') {
        posthog.opt_out_capturing();
    }
}

try {
    if (posthogKey) {
        posthog.init(posthogKey, {
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
            cross_subdomain_cookie: true,
            person_profiles: 'identified_only',
            capture_pageview: false,
            capture_pageleave: true,
            disable_session_recording: true,
            // Same model as kontomatch.com: nothing captured or stored until the
            // visitor answers the banner; after a reject, anonymous cookieless events.
            cookieless_mode: 'on_reject',
            // Click autocapture would send element text (vendor names, amounts).
            autocapture: false,
            // No feature flags are used.
            advanced_disable_flags: true,
            before_send: sanitizeEvent,
        });
        // A reject given on kontomatch.com must also apply here, and vice versa.
        applyStoredConsent();
    } else {
        console.error("PostHog API Key is missing! Please restart npm start or check .env file.");
    }
} catch (e) {
    console.error("PostHog initialization failed:", e);
}

export default posthog;
